import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CallBackProps, Step } from "react-joyride";
import { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import { ProjectAssetsStore } from "../../../state/ProjectAssetsStore";
import { getActivationFlowDefinition } from "./flowRegistry";
import {
    finishActivationRun,
    getActivationRun,
    markActivationRunRunning,
    setActivationRunStepIndex,
} from "./runStore";
import type {
    ActivationPreStepAction,
    ActivationProgressPayload,
    ActivationConditionKey,
    ActivationControllerState,
    ActivationFlowDefinition,
    ActivationRunRecord,
    ActivationRunStatus,
    ActivationStepDefinition,
} from "./types";

const TARGET_POLL_MS = 220;
const CONDITION_POLL_MS = 260;
const AUTO_ADVANCE_DELAY_MS = 360;
const INITIAL_FIRST_STEP_DELAY_MS = 1400;

interface UseActivationControllerArgs {
    runId?: string | null;
    onFinish?: () => void;
    onRunPreStepAction?: (action: ActivationPreStepAction) => void;
    onProgress?: (payload: ActivationProgressPayload) => void;
}

const toJoyrideSteps = (flow: ActivationFlowDefinition | null): Step[] => {
    if (!flow) return [];
    return flow.steps.map((step) => ({
        target: step.target,
        content: step.content,
        placement: (step.placement ?? "bottom") as Step["placement"],
        disableBeacon: true,
        spotlightPadding: step.spotlightPadding ?? 8,
        data: {
            title: step.title ?? "",
            requiresInteraction: step.advance.type !== "manual",
        },
    }));
};

const isConditionMet = (condition: ActivationConditionKey): boolean => {
    switch (condition) {
        case "studio_sound_ready": {
            const tracks = Array.isArray(ProjectAssetsStore.projectTracks)
                ? ProjectAssetsStore.projectTracks
                : [];
            return tracks.some(
                (track: any) =>
                    String(track?.enhancedTrackState || "")
                        .trim()
                        .toLowerCase() === "ready"
            );
        }
        case "script_has_cut":
            return Array.isArray(ScriptLayerStore.cuts) && ScriptLayerStore.cuts.length > 0;
        case "decompose_export_started":
            return document.querySelector("[data-tour='decompose-export-list']") != null;
        case "decompose_processing_complete": {
            const rows = Array.from(
                document.querySelectorAll("[data-tour='decompose-export-list'] [data-decompose-export-status]")
            );
            if (!rows.length) return false;
            const statuses = rows.map((row) =>
                String((row as HTMLElement).getAttribute("data-decompose-export-status") || "")
                    .trim()
                    .toLowerCase()
            );
            return statuses.every((status) => status !== "pending" && status !== "exporting");
        }
        case "create_sequence_action_visible":
            return document.querySelector("[data-tour='asset-popover-create-sequence']") != null;
        default:
            return false;
    }
};

export const useActivationController = ({
    runId,
    onFinish,
    onRunPreStepAction,
    onProgress,
}: UseActivationControllerArgs): ActivationControllerState => {
    const [flow, setFlow] = useState<ActivationFlowDefinition | null>(null);
    const [runRecord, setRunRecord] = useState<ActivationRunRecord | null>(null);
    const [stepIndex, setStepIndex] = useState(0);
    const [hasActiveRun, setHasActiveRun] = useState(false);
    const [isTargetReady, setIsTargetReady] = useState(false);
    const [clickedCurrentStep, setClickedCurrentStep] = useState(false);
    const [isInitialStepDelayReady, setIsInitialStepDelayReady] = useState(false);

    const lastPreActionStepRef = useRef<string>("");
    const advancingRef = useRef(false);
    const delayedAdvanceTimerRef = useRef<number | null>(null);
    const initialStepDelayTimerRef = useRef<number | null>(null);
    const initialStepDelayRunRef = useRef<string>("");
    const lastProgressSignatureRef = useRef<string>("");

    const joyrideSteps = useMemo(() => toJoyrideSteps(flow), [flow]);
    const activeStep: ActivationStepDefinition | null = useMemo(() => {
        if (!flow) return null;
        return flow.steps[stepIndex] ?? null;
    }, [flow, stepIndex]);

    const getStepAutoAdvanceDelay = useCallback((step: ActivationStepDefinition | null): number => {
        const configured = Number(step?.autoAdvanceDelayMs ?? Number.NaN);
        if (Number.isFinite(configured) && configured >= 0) return configured;
        return AUTO_ADVANCE_DELAY_MS;
    }, []);

    const clearPendingAutoAdvance = useCallback(() => {
        if (delayedAdvanceTimerRef.current == null) return;
        window.clearTimeout(delayedAdvanceTimerRef.current);
        delayedAdvanceTimerRef.current = null;
    }, []);

    const clearInitialStepDelayTimer = useCallback(() => {
        if (initialStepDelayTimerRef.current == null) return;
        window.clearTimeout(initialStepDelayTimerRef.current);
        initialStepDelayTimerRef.current = null;
    }, []);

    const emitProgress = useCallback(
        (
            status: ActivationRunStatus,
            forcedStepIndex?: number,
            forcedStep?: ActivationStepDefinition | null
        ) => {
            const safeRunId = String(runRecord?.id || "").trim();
            const safeFlowId = flow?.id;
            if (!safeRunId || !safeFlowId) return;

            const normalizedStepIndex = Number.isFinite(forcedStepIndex)
                ? Math.max(0, Math.floor(Number(forcedStepIndex)))
                : Math.max(0, Math.floor(Number(stepIndex) || 0));

            const resolvedStep =
                forcedStep ??
                activeStep ??
                flow?.steps?.[normalizedStepIndex] ??
                null;

            const payload: ActivationProgressPayload = {
                runId: safeRunId,
                flowId: safeFlowId,
                status,
                currentStepIndex: normalizedStepIndex,
                currentStepId: String(resolvedStep?.id || "").trim() || undefined,
                source: runRecord?.source,
                projectId: runRecord?.projectId,
            };

            const signature = [
                payload.runId,
                payload.flowId,
                payload.status,
                String(payload.currentStepIndex),
                payload.currentStepId || "",
            ].join("|");

            if (signature === lastProgressSignatureRef.current) return;
            lastProgressSignatureRef.current = signature;
            onProgress?.(payload);
        },
        [activeStep, flow, onProgress, runRecord?.id, runRecord?.projectId, runRecord?.source, stepIndex]
    );

    const finalizeRun = useCallback(
        (status: ActivationRunStatus) => {
            clearPendingAutoAdvance();
            clearInitialStepDelayTimer();
            emitProgress(status, stepIndex, activeStep);
            if (runRecord?.id) {
                finishActivationRun(runRecord.id, status);
            }
            setHasActiveRun(false);
            setFlow(null);
            setRunRecord(null);
            setStepIndex(0);
            setClickedCurrentStep(false);
            setIsTargetReady(false);
            setIsInitialStepDelayReady(false);
            onFinish?.();
        },
        [
            activeStep,
            clearInitialStepDelayTimer,
            clearPendingAutoAdvance,
            emitProgress,
            onFinish,
            runRecord?.id,
            stepIndex,
        ]
    );

    const advanceStep = useCallback(() => {
        if (!flow || !runRecord?.id) return;
        if (advancingRef.current) return;
        advancingRef.current = true;
        window.setTimeout(() => {
            advancingRef.current = false;
        }, 0);

        const nextStepIndex = stepIndex + 1;
        if (nextStepIndex >= flow.steps.length) {
            finalizeRun("completed");
            return;
        }

        setActivationRunStepIndex(runRecord.id, nextStepIndex);
        setStepIndex(nextStepIndex);
        setClickedCurrentStep(false);
    }, [flow, runRecord?.id, stepIndex, finalizeRun]);

    const regressStep = useCallback(() => {
        clearPendingAutoAdvance();
        if (!flow || !runRecord?.id) return;
        const prevStepIndex = Math.max(0, stepIndex - 1);
        setActivationRunStepIndex(runRecord.id, prevStepIndex);
        setStepIndex(prevStepIndex);
        setClickedCurrentStep(false);
    }, [clearPendingAutoAdvance, flow, runRecord?.id, stepIndex]);

    const scheduleAutoAdvanceStep = useCallback(
        (delayMs: number = AUTO_ADVANCE_DELAY_MS) => {
            if (delayedAdvanceTimerRef.current != null) return;
            delayedAdvanceTimerRef.current = window.setTimeout(() => {
                delayedAdvanceTimerRef.current = null;
                advanceStep();
            }, Math.max(0, Number(delayMs) || 0));
        },
        [advanceStep]
    );

    useEffect(() => {
        const safeRunId = String(runId || "").trim();
        if (!safeRunId) {
            clearPendingAutoAdvance();
            clearInitialStepDelayTimer();
            lastProgressSignatureRef.current = "";
            setHasActiveRun(false);
            setFlow(null);
            setRunRecord(null);
            setStepIndex(0);
            setClickedCurrentStep(false);
            setIsTargetReady(false);
            setIsInitialStepDelayReady(false);
            return;
        }

        const record = getActivationRun(safeRunId);
        if (!record) {
            lastProgressSignatureRef.current = "";
            setHasActiveRun(false);
            setFlow(null);
            setRunRecord(null);
            return;
        }

        if (record.status === "completed" || record.status === "skipped") {
            lastProgressSignatureRef.current = "";
            setHasActiveRun(false);
            setFlow(null);
            setRunRecord(record);
            return;
        }

        const flowDef = getActivationFlowDefinition(record.flowId);
        if (!flowDef || !flowDef.steps.length) {
            finishActivationRun(record.id, "failed");
            setHasActiveRun(false);
            setFlow(null);
            setRunRecord(record);
            return;
        }

        const started = markActivationRunRunning(record.id) ?? record;
        const persistedStep = Number(started.currentStepIndex ?? 0);
        const clampedStep = Math.max(
            0,
            Math.min(flowDef.steps.length - 1, Number.isFinite(persistedStep) ? persistedStep : 0)
        );

        setRunRecord(started);
        setFlow(flowDef);
        setStepIndex(clampedStep);
        setClickedCurrentStep(false);
        setHasActiveRun(true);
    }, [runId, clearInitialStepDelayTimer, clearPendingAutoAdvance]);

    useEffect(() => {
        if (!hasActiveRun || !runRecord?.id || !flow?.id) return;
        emitProgress("running", stepIndex, activeStep);
    }, [hasActiveRun, runRecord?.id, flow?.id, stepIndex, activeStep, emitProgress]);

    useEffect(() => {
        clearPendingAutoAdvance();
        setClickedCurrentStep(false);
    }, [activeStep?.id, clearPendingAutoAdvance]);

    useEffect(() => {
        if (!hasActiveRun || !runRecord?.id) {
            clearInitialStepDelayTimer();
            setIsInitialStepDelayReady(false);
            return;
        }

        if (stepIndex > 0 || initialStepDelayRunRef.current === runRecord.id) {
            clearInitialStepDelayTimer();
            setIsInitialStepDelayReady(true);
            return;
        }

        setIsInitialStepDelayReady(false);
        clearInitialStepDelayTimer();
        initialStepDelayTimerRef.current = window.setTimeout(() => {
            initialStepDelayRunRef.current = runRecord.id;
            initialStepDelayTimerRef.current = null;
            setIsInitialStepDelayReady(true);
        }, INITIAL_FIRST_STEP_DELAY_MS);

        return () => {
            clearInitialStepDelayTimer();
        };
    }, [hasActiveRun, runRecord?.id, stepIndex, clearInitialStepDelayTimer]);

    useEffect(
        () => () => {
            clearPendingAutoAdvance();
            clearInitialStepDelayTimer();
        },
        [clearInitialStepDelayTimer, clearPendingAutoAdvance]
    );

    useEffect(() => {
        if (!hasActiveRun || !activeStep) {
            setIsTargetReady(false);
            return;
        }

        const runPreStepAction = () => {
            const preActionKey = `${runRecord?.id || ""}:${activeStep.id}`;
            if (lastPreActionStepRef.current === preActionKey) return;
            if (activeStep.preStepAction) {
                onRunPreStepAction?.(activeStep.preStepAction);
            }
            lastPreActionStepRef.current = preActionKey;
        };

        const checkTarget = () => {
            runPreStepAction();
            const found = document.querySelector(activeStep.target) != null;
            setIsTargetReady(found);
        };

        checkTarget();
        const intervalId = window.setInterval(checkTarget, TARGET_POLL_MS);
        return () => {
            window.clearInterval(intervalId);
        };
    }, [hasActiveRun, activeStep, onRunPreStepAction, runRecord?.id]);

    useEffect(() => {
        if (!hasActiveRun || !activeStep) return;
        const advance = activeStep.advance;
        if (advance.type !== "click" && advance.type !== "click_then_condition") return;

        const selector = advance.selector;
        const handlePointerDown = (event: Event) => {
            const target = event.target as Element | null;
            if (!target || typeof target.closest !== "function") return;
            const matched = target.closest(selector);
            if (!matched) return;
            if (advance.type === "click") {
                scheduleAutoAdvanceStep(getStepAutoAdvanceDelay(activeStep));
                return;
            }
            setClickedCurrentStep(true);
        };
        document.addEventListener("pointerdown", handlePointerDown, true);
        return () => {
            document.removeEventListener("pointerdown", handlePointerDown, true);
        };
    }, [hasActiveRun, activeStep, getStepAutoAdvanceDelay, scheduleAutoAdvanceStep]);

    useEffect(() => {
        if (!hasActiveRun || !activeStep) return;
        const advance = activeStep.advance;
        if (advance.type !== "condition" && advance.type !== "click_then_condition") return;

        const intervalId = window.setInterval(() => {
            if (advance.type === "click_then_condition" && !clickedCurrentStep) return;
            if (isConditionMet(advance.condition)) {
                scheduleAutoAdvanceStep(getStepAutoAdvanceDelay(activeStep));
            }
        }, CONDITION_POLL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [hasActiveRun, activeStep, clickedCurrentStep, getStepAutoAdvanceDelay, scheduleAutoAdvanceStep]);

    const onJoyrideCallback = useCallback(
        (data: CallBackProps) => {
            if (data.status === STATUS.SKIPPED) {
                finalizeRun("skipped");
                return;
            }
            if (data.action === ACTIONS.SKIP || data.action === ACTIONS.CLOSE) {
                finalizeRun("skipped");
                return;
            }
            if (
                data.status === STATUS.FINISHED ||
                data.type === EVENTS.TOUR_END
            ) {
                finalizeRun("completed");
                return;
            }

            if (
                data.type === EVENTS.STEP_AFTER &&
                typeof data.index === "number" &&
                data.index !== stepIndex
            ) {
                return;
            }

            if (data.type === EVENTS.STEP_AFTER && data.action === ACTIONS.PREV) {
                regressStep();
                return;
            }

            if (data.type === EVENTS.STEP_AFTER && data.action === ACTIONS.NEXT) {
                if (!activeStep) return;
                const advance = activeStep.advance;
                if (advance.type === "manual") {
                    advanceStep();
                    return;
                }
                if (advance.type === "click") {
                    return;
                }
                if (advance.type === "condition" && !isConditionMet(advance.condition)) {
                    return;
                }
                if (advance.type === "click_then_condition") {
                    if (!clickedCurrentStep) return;
                    if (!isConditionMet(advance.condition)) return;
                }
                advanceStep();
            }
        },
        [finalizeRun, regressStep, activeStep, clickedCurrentStep, advanceStep, stepIndex]
    );

    return {
        hasActiveRun,
        shouldRunJoyride: hasActiveRun && isTargetReady && isInitialStepDelayReady,
        stepIndex,
        steps: joyrideSteps,
        onJoyrideCallback,
    };
};
